import React, { useState, useEffect } from 'react';
import {
  MessageCircle,
  CheckCircle2,
  AlertCircle,
  ShieldCheck,
  RefreshCw,
  LogOut,
  ExternalLink,
  BookOpen,
  ChevronDown,
  Smartphone,
  Cloud,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { WhatsAppCloudGuideModal } from './WhatsAppCloudGuideModal';
import {
  startWhatsAppEmbeddedSignup,
  completeWhatsAppEmbeddedSignup,
  getWhatsAppAccountStatus,
  disconnectWhatsAppAccount,
  WhatsAppStatusResponse,
} from '@/services/whatsappEmbeddedApi';

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

interface WhatsAppEmbeddedSignupProps {
  onSuccess?: (result?: { channelInstanceId?: string }) => void;
  className?: string;
  compact?: boolean;
  initialCheckingStatus?: boolean;
}

export const WhatsAppEmbeddedSignupButton: React.FC<WhatsAppEmbeddedSignupProps> = ({
  onSuccess,
  className = '',
  compact = false,
  initialCheckingStatus = true,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [statusStep, setStatusStep] = useState<string | null>(null);
  const [onboardingMode, setOnboardingMode] = useState<'coexistence' | 'standard'>('coexistence');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<WhatsAppStatusResponse | null>(null);
  const [checkingInitialStatus, setCheckingInitialStatus] = useState<boolean>(initialCheckingStatus);
  const [isGuideOpen, setIsGuideOpen] = useState<boolean>(false);

  const fetchStatus = async () => {
    try {
      const res = await getWhatsAppAccountStatus();
      setAccountStatus(res);
    } catch {
      // Ignore on mount
    } finally {
      setCheckingInitialStatus(false);
    }
  };

  useEffect(() => {
    void fetchStatus();
  }, []);

  const handleStartSignup = async () => {
    setLoading(true);
    setErrorMessage(null);
    setStatusStep('Iniciando sesión segura...');

    try {
      // 1. Obtener state nonce y credenciales de la app desde el backend
      const session = await startWhatsAppEmbeddedSignup();
      if (!session.appId || !session.configId) {
        throw new Error(
          'Faltan META_APP_ID o META_EMBEDDED_SIGNUP_CONFIG_ID en la configuración del servidor.'
        );
      }

      // 2. Preparar redirect_uri determinista (100% idéntico entre el diálogo OAuth y el intercambio en backend)
      const redirectUri = `${window.location.origin}/`;
      let signupData: { phone_number_id?: string; waba_id?: string; business_id?: string } | null = null;
      let isFinalizing = false;
      let popupWindow: Window | null = null;
      let popupCheckInterval: ReturnType<typeof setInterval> | null = null;
      let broadcastChannel: BroadcastChannel | null = null;

      const cleanupListeners = () => {
        window.removeEventListener('message', messageListener);
        window.removeEventListener('storage', storageListener);
        if (popupCheckInterval) {
          clearInterval(popupCheckInterval);
          popupCheckInterval = null;
        }
        if (broadcastChannel) {
          try {
            broadcastChannel.close();
          } catch {
            // ignore
          }
          broadcastChannel = null;
        }
      };

      const triggerFinalizeWithCode = (code: string, callbackRedirectUri?: string) => {
        if (isFinalizing || !code) return;
        isFinalizing = true;
        cleanupListeners();
        try {
          if (popupWindow && !popupWindow.closed) {
            popupWindow.close();
          }
        } catch {
          // ignore
        }
        void finalizeSignup(
          session.state,
          code,
          signupData?.waba_id,
          signupData?.phone_number_id,
          signupData?.business_id,
          callbackRedirectUri || redirectUri
        );
      };

      const handleOAuthCallbackPayload = (payload: {
        type?: string;
        code?: string;
        state?: string;
        redirectUri?: string;
        error?: string;
      }) => {
        if (!payload || payload.type !== 'IRIDIA_META_OAUTH_CALLBACK') return;
        if (payload.state !== session.state) return;

        if (payload.error) {
          cleanupListeners();
          setLoading(false);
          setStatusStep(null);
          setErrorMessage(`Autorización cancelada o rechazada por Meta: ${payload.error}`);
          return;
        }

        if (payload.code) {
          triggerFinalizeWithCode(payload.code, payload.redirectUri || redirectUri);
        }
      };

      const messageListener = (event: MessageEvent) => {
        try {
          const payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

          if (payload?.type === 'IRIDIA_META_OAUTH_CALLBACK') {
            handleOAuthCallbackPayload(payload);
            return;
          }

          if (!event.origin.includes('facebook.com')) return;
          console.log('[Meta Embedded Signup] postMessage event:', payload);

          if (payload?.type === 'WA_EMBEDDED_SIGNUP') {
            if (payload.event === 'FINISH' || payload.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING') {
              signupData = payload.data || {};
              setStatusStep('Datos recibidos de Meta. Finalizando vinculación...');
            } else if (payload.event === 'CANCEL') {
              cleanupListeners();
              setLoading(false);
              setStatusStep(null);
              setErrorMessage('El registro fue cancelado en la ventana de Meta.');
            } else if (payload.event === 'ERROR') {
              cleanupListeners();
              setLoading(false);
              setStatusStep(null);
              setErrorMessage(`Error en el asistente de Meta: ${payload.data?.error_message || 'No se pudo vincular la cuenta'}`);
            }
          }
        } catch {
          // ignore non-json messages
        }
      };

      const storageListener = (event: StorageEvent) => {
        if (event.key === 'iridia_meta_oauth_callback' && event.newValue) {
          try {
            const payload = JSON.parse(event.newValue);
            handleOAuthCallbackPayload(payload);
          } catch {
            // ignore
          }
        }
      };

      window.addEventListener('message', messageListener);
      window.addEventListener('storage', storageListener);

      if (typeof BroadcastChannel !== 'undefined') {
        try {
          broadcastChannel = new BroadcastChannel('iridia_meta_oauth');
          broadcastChannel.onmessage = (event: MessageEvent) => {
            handleOAuthCallbackPayload(event.data);
          };
        } catch {
          // ignore
        }
      }

      // 3. Abrir ventana emergente de OAuth de Meta con redirect_uri exacto
      setStatusStep('Completa los pasos en la ventana emergente de Meta...');
      const graphVersion = session.graphVersion || 'v21.0';
      const oauthUrl = new URL(`https://www.facebook.com/${graphVersion}/dialog/oauth`);
      oauthUrl.searchParams.set('client_id', session.appId);
      oauthUrl.searchParams.set('redirect_uri', redirectUri);
      oauthUrl.searchParams.set('config_id', session.configId);
      oauthUrl.searchParams.set('response_type', 'code');
      oauthUrl.searchParams.set('override_default_response_type', 'true');
      oauthUrl.searchParams.set('auth_type', 'rerequest');
      oauthUrl.searchParams.set('state', session.state);
      const extrasPayload: Record<string, any> = {
        setup: {},
        sessionInfoVersion: '3',
      };
      if (onboardingMode === 'coexistence') {
        extrasPayload.featureType = 'whatsapp_business_app_onboarding';
      }
      oauthUrl.searchParams.set('extras', JSON.stringify(extrasPayload));

      const width = 620;
      const height = 720;
      const left = Math.max(0, Math.round(window.screenX + (window.outerWidth - width) / 2));
      const top = Math.max(0, Math.round(window.screenY + (window.outerHeight - height) / 2));

      popupWindow = window.open(
        oauthUrl.toString(),
        'MetaEmbeddedSignup',
        `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );

      if (!popupWindow) {
        cleanupListeners();
        throw new Error('El navegador bloqueó la ventana emergente. Por favor permite ventanas emergentes para este sitio.');
      }

      // Vigilar si el usuario cierra la ventana manualmente sin completar el flujo
      popupCheckInterval = setInterval(() => {
        if (popupWindow && popupWindow.closed) {
          if (popupCheckInterval) {
            clearInterval(popupCheckInterval);
            popupCheckInterval = null;
          }
          setTimeout(() => {
            if (!isFinalizing) {
              cleanupListeners();
              setLoading(false);
              setStatusStep(null);
            }
          }, 1200);
        }
      }, 600);
    } catch (err: unknown) {
      setLoading(false);
      setStatusStep(null);
      const errObj = err as { response?: { data?: { message?: string } }; message?: string };
      setErrorMessage(errObj?.response?.data?.message || errObj?.message || 'Error iniciando conexión con WhatsApp');
    }
  };

  const finalizeSignup = async (
    state: string,
    code: string,
    wabaId?: string,
    phoneNumberId?: string,
    businessId?: string,
    redirectUri?: string
  ) => {
    setStatusStep('Verificando cuenta y suscribiendo webhooks...');
    try {
      const result = await completeWhatsAppEmbeddedSignup({
        state,
        code,
        wabaId: wabaId || undefined,
        phoneNumberId: phoneNumberId || undefined,
        businessId: businessId || undefined,
        redirectUri: redirectUri || undefined,
      });

      setStatusStep('¡Conexión completada con éxito!');
      await fetchStatus();
      onSuccess?.(result);
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { message?: string } }; message?: string };
      setErrorMessage(
        errObj?.response?.data?.message ||
          errObj?.message ||
          'No se pudo completar el registro con Meta. Por favor intenta de nuevo.'
      );
    } finally {
      setLoading(false);
      setTimeout(() => setStatusStep(null), 3000);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Seguro que deseas desconectar tu cuenta de WhatsApp? El bot dejará de responder llamadas y mensajes en esta línea.')) {
      return;
    }
    setLoading(true);
    try {
      await disconnectWhatsAppAccount();
      await fetchStatus();
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { message?: string } } };
      alert(errObj?.response?.data?.message || 'Error al desconectar la cuenta');
    } finally {
      setLoading(false);
    }
  };

  if (checkingInitialStatus) {
    return (
      <div className={`flex items-center justify-center ${compact ? 'p-4' : 'p-6'} bg-white rounded-xl border border-gray-200`}>
        <RefreshCw className="h-5 w-5 animate-spin text-emerald-600 mr-2" />
        <span className="text-sm text-gray-600">Verificando estado de WhatsApp...</span>
      </div>
    );
  }

  // Si ya está conectado, mostrar ficha de estado
  if (accountStatus?.connected && accountStatus.account) {
    const acc = accountStatus.account;
    return (
      <div className={`rounded-xl border border-emerald-200 bg-emerald-50/50 ${compact ? 'p-4' : 'p-6'} ${className}`}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`${compact ? 'p-2.5' : 'p-3'} bg-emerald-600 text-white rounded-xl shadow-sm shrink-0`}>
              <MessageCircle className={compact ? 'h-5 w-5' : 'h-6 w-6'} />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="text-sm sm:text-base font-semibold text-gray-900">
                  {acc.displayPhoneNumber || 'WhatsApp Business Conectado'}
                </h4>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Activo
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-1 break-all">
                ID de Número: <span className="font-mono">{acc.phoneNumberId}</span> • WABA ID: <span className="font-mono">{acc.wabaId}</span>
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {acc.qualityRating && (
                  <span className="text-xs px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-700">
                    Calidad: <strong>{acc.qualityRating}</strong>
                  </span>
                )}
                {acc.messagingLimitTier && (
                  <span className="text-xs px-2 py-0.5 rounded bg-white border border-gray-200 text-gray-700">
                    Nivel: <strong>{acc.messagingLimitTier}</strong>
                  </span>
                )}
              </div>

              {(acc.nameStatus === 'NOT_VERIFIED' || acc.phoneStatus === 'DISCONNECTED') && (
                <div className="mt-3 p-3 rounded-xl bg-amber-50/90 border border-amber-300 text-xs text-amber-950 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-bold text-amber-900">
                        Vinculación o escaneo QR pendiente en Meta (Modo Coexistencia):
                      </span>
                      <p className="mt-0.5 text-amber-800 leading-relaxed">
                        Este número figura como <strong>NOT_VERIFIED / DISCONNECTED</strong> en Meta porque Meta no mostró el código QR (el número ya estaba precargado en la cuenta de WhatsApp de Meta en un intento previo).
                      </p>
                    </div>
                  </div>

                  <div className="bg-white/90 p-2.5 rounded-lg border border-amber-200 space-y-1.5 text-gray-800">
                    <p className="font-semibold text-gray-900">¿Cómo hacer que Meta muestre el código QR de nuevo?</p>
                    <ol className="list-decimal list-inside space-y-1 text-gray-700">
                      <li>
                        Haz clic en <strong>Desconectar</strong> abajo.
                      </li>
                      <li>
                        Al hacer clic en <strong>Conectar con Meta</strong>, cuando la ventana emergente pregunte <em>&ldquo;Selecciona una cuenta de WhatsApp Business&rdquo;</em>, elige <strong>&ldquo;Crear una nueva cuenta de WhatsApp Business&rdquo;</strong>.
                      </li>
                      <li>
                        Al crear una cuenta nueva, Meta presentará inmediatamente el <strong>Paso 2 (tu número)</strong> y el <strong>Paso 3 (Código QR para escanear en WhatsApp Business)</strong>.
                      </li>
                    </ol>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-[11px] text-amber-800">
                    <span>O elimina el número atascado en Meta:</span>
                    <a
                      href={`https://business.facebook.com/wa/manage/phone-numbers/?waba_id=${acc.wabaId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold hover:text-amber-950 inline-flex items-center gap-1"
                    >
                      WhatsApp Manager en Meta <ExternalLink className="h-3 w-3 inline" />
                    </a>
                  </div>
                </div>
              )}

              {(!acc.channelType || acc.channelType === 'WHATSAPP_CLOUD' || acc.provider === 'meta_direct') &&
                acc.channelType !== 'WHATSAPP_BAILEYS' &&
                acc.channelType !== 'WHATSAPP_WEB' &&
                acc.hasPaymentMethod === false && (
                <div className="mt-2.5 p-2 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-2 text-xs text-amber-800">
                  <AlertCircle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-semibold">Sin método de pago en Meta: </span>
                    Para enviar plantillas masivas y mensajes fuera de 24h, asocia una tarjeta en tu{' '}
                    <a
                      href={`https://business.facebook.com/wa/manage/home/?waba_id=${acc.wabaId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline font-semibold hover:text-amber-900 inline-flex items-center gap-0.5"
                    >
                      Meta Business Suite <ExternalLink className="h-3 w-3 inline" />
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={async () => {
                setLoading(true);
                try {
                  const res = await getWhatsAppAccountStatus({ refresh: true });
                  setAccountStatus(res);
                } catch {
                  // ignore
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="text-gray-700 hover:text-gray-900 hover:bg-gray-100 border-gray-200 text-xs"
              title="Consultar estado actualizado en los servidores de Meta"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Re-verificar
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleDisconnect}
              disabled={loading}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 text-xs"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              Desconectar
            </Button>
          </div>
        </div>

        <div className={`${compact ? 'mt-3 pt-3' : 'mt-4 pt-4'} border-t border-emerald-100 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500`}>
          <div className="flex items-center gap-3">
            <span>Conexión directa vía Meta Tech Provider (Cloud API)</span>
            <button
              type="button"
              onClick={() => setIsGuideOpen(true)}
              className="text-emerald-700 hover:underline inline-flex items-center gap-1 font-semibold"
            >
              <BookOpen className="h-3 w-3" /> Guía & Políticas
            </button>
          </div>
          <a
            href="https://business.facebook.com/wa/manage/home"
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-700 hover:underline inline-flex items-center gap-1 font-medium"
          >
            Administrar en Meta Business <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <WhatsAppCloudGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
      </div>
    );
  }

  // Estado desconectado: Mostrar botón de Embedded Signup
  if (compact) {
    return (
      <div className={`rounded-xl border border-gray-200 bg-white p-4 shadow-xs space-y-3.5 ${className}`}>
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-700 shrink-0">
              <MessageCircle className="h-4 w-4" />
            </div>
            <h4 className="text-sm font-bold text-gray-900">
              Conectar con WhatsApp Oficial (Meta)
            </h4>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">
            Inicia sesión con tu cuenta de Facebook/Meta para vincular tu número de WhatsApp Business en menos de 2 minutos sin intermediarios.
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> Verificación oficial de Meta
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> Sin escanear QR diarios
            </span>
          </div>
        </div>

        {/* Acceso destacado a Guía Oficial Paso a Paso y Escenarios */}
        <div className="p-2.5 rounded-xl bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 border border-emerald-200/90 flex items-center justify-between gap-2 shadow-2xs">
          <div className="flex items-center gap-2 min-w-0">
            <div className="p-1.5 rounded-lg bg-emerald-100 text-emerald-800 shrink-0">
              <BookOpen className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-emerald-950 truncate">
                ¿Dudas con tu número o catálogos?
              </p>
              <p className="text-[11px] text-emerald-700 truncate">
                Escenarios QR vs Cloud, 1.000 chats gratis y FAQ
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsGuideOpen(true)}
            className="px-2.5 py-1.5 rounded-lg bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold shrink-0 transition-colors flex items-center gap-1 shadow-2xs"
          >
            <span>Ver Guía</span>
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>

        {/* Selector de modo oficial de Meta (Idéntico a Kommo: Coexistencia vs Cloud Estándar) */}
        <div className="space-y-1.5 pt-0.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold text-gray-800">
              ¿Qué WhatsApp estás utilizando?
            </label>
            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
              Oficial de Meta
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setOnboardingMode('coexistence')}
              className={`p-3 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-1.5 ${
                onboardingMode === 'coexistence'
                  ? 'border-emerald-600 bg-emerald-50/80 shadow-xs ring-2 ring-emerald-600/20'
                  : 'border-gray-200 bg-white hover:border-emerald-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 font-bold text-xs text-gray-900">
                  <Smartphone className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>App WhatsApp Business</span>
                </div>
                {onboardingMode === 'coexistence' && (
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                )}
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                Sigue usando tu app en el celular con tus chats y catálogo. Vinculación por Código QR de Meta.
              </p>
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 w-fit">
                Modo Coexistencia
              </span>
            </button>

            <button
              type="button"
              onClick={() => setOnboardingMode('standard')}
              className={`p-3 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-1.5 ${
                onboardingMode === 'standard'
                  ? 'border-blue-600 bg-blue-50/80 shadow-xs ring-2 ring-blue-600/20'
                  : 'border-gray-200 bg-white hover:border-blue-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 font-bold text-xs text-gray-900">
                  <Cloud className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>Cloud API Estándar</span>
                </div>
                {onboardingMode === 'standard' && (
                  <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
                )}
              </div>
              <p className="text-[11px] text-gray-600 leading-snug">
                Para números nuevos, líneas fijas o gestión exclusiva desde la plataforma web (SMS / Llamada).
              </p>
              <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800 w-fit">
                Solo Plataforma
              </span>
            </button>
          </div>
        </div>

        {/* Recomendaciones colapsables para el proceso de vinculación */}
        <details className="group rounded-xl border border-blue-100 bg-blue-50/60 p-2.5 text-xs text-blue-900 transition-all">
          <summary className="flex items-center justify-between cursor-pointer font-semibold text-blue-950 list-none select-none">
            <span className="flex items-center gap-1.5">
              <span>💡</span> Pasos clave en la ventana de Meta ({onboardingMode === 'coexistence' ? 'Coexistencia' : 'Cloud Estándar'})
            </span>
            <span className="text-[11px] font-medium text-blue-700 bg-blue-100/70 hover:bg-blue-100 px-2 py-0.5 rounded-md flex items-center gap-1 transition-colors">
              <span className="group-open:hidden">Ver tips</span>
              <span className="hidden group-open:inline">Ocultar</span>
              <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-open:rotate-180" />
            </span>
          </summary>
          <ul className="mt-2.5 list-disc pl-4 space-y-1 text-blue-800 leading-relaxed border-t border-blue-100/80 pt-2">
            <li>
              <strong>Selecciona tu Cuenta de Empresa:</strong> Elige tu Portafolio Comercial (ej. el nombre de tu negocio), no tu perfil personal.
            </li>
            <li>
              <strong>¿Ya te habías conectado antes?</strong> Haz clic en el botón blanco de la izquierda <strong>&ldquo;Editar configuración&rdquo;</strong> (no en <em>Continuar</em>) para elegir tu empresa y marcar tu cuenta de WhatsApp.
            </li>
            <li>
              <strong>Permisos de WhatsApp:</strong> Selecciona <em>&ldquo;Activar todas las cuentas actuales y futuras&rdquo;</em> o marca las casillas de tu empresa.
            </li>
            {onboardingMode === 'coexistence' ? (
              <li>
                <strong>Coexistencia con Código QR:</strong> Cuando Meta pregunte <em>&ldquo;Selecciona una cuenta de WhatsApp Business&rdquo;</em>, elige <strong>&ldquo;Crear una nueva cuenta de WhatsApp Business&rdquo;</strong> para que Meta muestre el código QR de vinculación y no herede números atascados.
              </li>
            ) : (
              <li>
                <strong>Verificación por SMS/Llamada:</strong> Meta te enviará un código de verificación de 6 dígitos por mensaje de texto o llamada a tu número.
              </li>
            )}
            <li>
              <strong>Al finalizar:</strong> Cuando aparezca el mensaje con confeti <em>&ldquo;Se conectó a IridiaChat&rdquo;</em>, <strong>cierra esa ventana emergente (X)</strong> para que el sistema complete la vinculación automáticamente.
            </li>
          </ul>
        </details>

        <div className="flex flex-col gap-1.5">
          <Button
            type="button"
            onClick={handleStartSignup}
            disabled={loading}
            className="w-full h-11 px-4 font-semibold bg-[#1877F2] hover:bg-[#166fe5] text-white shadow-sm hover:shadow-md transition-all rounded-xl flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>{statusStep || 'Conectando...'}</span>
              </>
            ) : (
              <>
                <MessageCircle className="h-4 w-4" />
                <span>Conectar con WhatsApp</span>
              </>
            )}
          </Button>

          {statusStep && (
            <span className="text-xs text-center text-emerald-700 font-medium animate-pulse">
              {statusStep}
            </span>
          )}
        </div>

        {errorMessage && (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2.5">
            <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-800">
              <p className="font-semibold">No se pudo completar la conexión</p>
              <p className="mt-0.5">{errorMessage}</p>
            </div>
          </div>
        )}

        <WhatsAppCloudGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-gray-200 bg-white p-6 shadow-sm space-y-5 ${className}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-emerald-100 text-emerald-700">
              <MessageCircle className="h-5 w-5" />
            </div>
            <h4 className="text-lg font-bold text-gray-900">
              Conectar con WhatsApp Oficial (Meta)
            </h4>
          </div>
          <p className="text-sm text-gray-600 max-w-xl">
            Inicia sesión con tu cuenta de Facebook/Meta para vincular tu número de WhatsApp Business en menos de 2 minutos sin intermediarios ni cobros extra por mensaje.
          </p>

          <div className="flex flex-wrap items-center gap-4 pt-1 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <ShieldCheck className="h-4 w-4 text-emerald-600" /> Verificación oficial de Meta
            </span>
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Sin escanear QR diarios
            </span>
          </div>
        </div>

        <div className="flex flex-col items-start md:items-end gap-2 flex-shrink-0">
          <Button
            type="button"
            onClick={handleStartSignup}
            disabled={loading}
            className="h-12 px-6 font-semibold bg-[#1877F2] hover:bg-[#166fe5] text-white shadow-md hover:shadow-lg transition-all rounded-xl flex items-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span>{statusStep || 'Conectando...'}</span>
              </>
            ) : (
              <>
                <MessageCircle className="h-5 w-5" />
                <span>Conectar con WhatsApp</span>
              </>
            )}
          </Button>

          {statusStep && (
            <span className="text-xs text-emerald-700 font-medium animate-pulse">
              {statusStep}
            </span>
          )}
        </div>
      </div>

      {/* Banner de acceso a la Guía Oficial y Escenarios */}
      <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 border border-emerald-200/90 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-2xs">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-emerald-100 text-emerald-800 shrink-0 mt-0.5">
            <BookOpen className="h-5 w-5" />
          </div>
          <div>
            <h5 className="font-bold text-gray-900 text-sm">
              Guía Oficial de Conexión: Escenarios, Costos de Meta & Políticas
            </h5>
            <p className="text-xs text-gray-600 mt-0.5 max-w-xl leading-relaxed">
              ¿Ya tienes WhatsApp Business en tu celular con catálogo? ¿Cómo funcionan las 1.000 conversaciones gratis de Meta? Revisa todos los escenarios y preguntas frecuentes.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => setIsGuideOpen(true)}
          className="border-emerald-400 text-emerald-800 hover:bg-emerald-100 font-semibold text-xs shrink-0 flex items-center gap-1.5 bg-white shadow-2xs"
        >
          <BookOpen className="h-4 w-4" />
          <span>Ver Guía Completa</span>
        </Button>
      </div>

      {errorMessage && (
        <div className="p-3.5 rounded-lg bg-red-50 border border-red-200 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">
            <p className="font-semibold">No se pudo completar la conexión</p>
            <p className="text-xs mt-0.5">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Selector de modo oficial de Meta en vista extendida (Idéntico a Kommo) */}
      <div className="space-y-2 p-4 rounded-xl border border-gray-200 bg-gray-50/50">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-gray-800">
            ¿Qué WhatsApp estás utilizando?
          </label>
          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            Ambos 100% Oficiales de Meta
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setOnboardingMode('coexistence')}
            className={`p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-1.5 ${
              onboardingMode === 'coexistence'
                ? 'border-emerald-600 bg-emerald-50/80 shadow-xs ring-2 ring-emerald-600/20'
                : 'border-gray-200 bg-white hover:border-emerald-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold text-xs text-gray-900">
                <Smartphone className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>Aplicación de WhatsApp Business</span>
              </div>
              {onboardingMode === 'coexistence' && (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              )}
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Sigue usando tu app en el celular con tus chats y catálogo móvil. Vinculación por Código QR oficial de Meta.
            </p>
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800 w-fit">
              Modo Coexistencia
            </span>
          </button>

          <button
            type="button"
            onClick={() => setOnboardingMode('standard')}
            className={`p-3.5 rounded-xl border-2 text-left transition-all flex flex-col justify-between gap-1.5 ${
              onboardingMode === 'standard'
                ? 'border-blue-600 bg-blue-50/80 shadow-xs ring-2 ring-blue-600/20'
                : 'border-gray-200 bg-white hover:border-blue-300'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-bold text-xs text-gray-900">
                <Cloud className="w-4 h-4 text-blue-600 shrink-0" />
                <span>WhatsApp Cloud API Estándar</span>
              </div>
              {onboardingMode === 'standard' && (
                <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0" />
              )}
            </div>
            <p className="text-xs text-gray-600 leading-relaxed">
              Para números nuevos, fijos o si deseas gestionar los mensajes exclusivamente desde IridiaChat (SMS / Llamada).
            </p>
            <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800 w-fit">
              Solo Plataforma Web
            </span>
          </button>
        </div>
      </div>

      {/* Recomendaciones colapsables para el proceso de vinculación */}
      <details className="group rounded-xl border border-blue-100 bg-blue-50/60 p-3 text-xs text-blue-900 transition-all">
        <summary className="flex items-center justify-between cursor-pointer font-semibold text-blue-950 list-none select-none">
          <span className="flex items-center gap-1.5">
            <span>💡</span> Pasos clave en la ventana emergente de Meta ({onboardingMode === 'coexistence' ? 'Coexistencia' : 'Cloud Estándar'})
          </span>
          <span className="text-[11px] font-medium text-blue-700 bg-blue-100/70 hover:bg-blue-100 px-2 py-0.5 rounded-md flex items-center gap-1 transition-colors">
            <span className="group-open:hidden">Ver tips</span>
            <span className="hidden group-open:inline">Ocultar</span>
            <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-open:rotate-180" />
          </span>
        </summary>
        <ul className="mt-2.5 list-disc pl-4 space-y-1 text-blue-800 leading-relaxed border-t border-blue-100/80 pt-2">
          <li>
            <strong>Selecciona tu Cuenta de Empresa:</strong> Elige tu Portafolio Comercial (ej. el nombre de tu negocio), no tu perfil personal.
          </li>
          <li>
            <strong>¿Ya te habías conectado antes?</strong> Haz clic en el botón blanco de la izquierda <strong>&ldquo;Editar configuración&rdquo;</strong> (no en <em>Continuar</em>) para elegir tu empresa y marcar tu cuenta de WhatsApp.
          </li>
          <li>
            <strong>Permisos de WhatsApp:</strong> Selecciona <em>&ldquo;Activar todas las cuentas actuales y futuras&rdquo;</em> o marca las casillas de tu empresa.
          </li>
          {onboardingMode === 'coexistence' ? (
            <li>
              <strong>Coexistencia con Código QR:</strong> Cuando Meta pregunte <em>&ldquo;Selecciona una cuenta de WhatsApp Business&rdquo;</em>, elige <strong>&ldquo;Crear una nueva cuenta de WhatsApp Business&rdquo;</strong> para que Meta muestre el código QR de vinculación y no herede números atascados.
            </li>
          ) : (
            <li>
              <strong>Verificación por SMS/Llamada:</strong> Meta te enviará un código de verificación de 6 dígitos por mensaje de texto o llamada a tu número.
            </li>
          )}
          <li>
            <strong>Al finalizar:</strong> Cuando aparezca el mensaje con confeti <em>&ldquo;Se conectó a IridiaChat&rdquo;</em>, <strong>cierra esa ventana emergente (X)</strong> para que el sistema complete la vinculación automáticamente.
          </li>
        </ul>
      </details>

      <WhatsAppCloudGuideModal isOpen={isGuideOpen} onClose={() => setIsGuideOpen(false)} />
    </div>
  );
};

export default WhatsAppEmbeddedSignupButton;
